/**
 * Thin view over `useMe()` — the single source of truth for "who am
 * I" + "which tenant" + "what can I do". No parallel state store.
 *
 * Roles are read from the Keycloak token's realm-access claim (set
 * by relay-api's `AuthedUser` extractor contract); `/me` populates
 * the `users` row but the role on the JWT is canonical for UI gating.
 */

import { useKeycloak } from './KeycloakProvider';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { clearTokens } from './tokenStore';
import { useMe, type MeAgency, type MeUser } from './useMe';

const E2E_MODE = import.meta.env.VITE_E2E_MODE === 'true';

interface AuthValue {
  isAuthenticated: boolean;
  user?: MeUser;
  tenant?: MeAgency;
  roles: string[];
  hasRole: (...allowed: string[]) => boolean;
  /**
   * v0.0.66.1 — check whether the current user has access to a named
   * backend feature, accounting for per-user overrides in
   * `user_feature_overrides`. Falls back to `true` when the `/me`
   * payload doesn't carry the map yet (older deploy / loading state) —
   * the backend re-checks the gate on every request regardless, so
   * leaning permissive here only affects the visible-button question.
   */
  hasFeature: (feature: string) => boolean;
  signOut: () => void;
}

export const AuthContext = createContext<AuthValue>({
  isAuthenticated: false,
  roles: [],
  hasRole: () => false,
  hasFeature: () => true,
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  return E2E_MODE ? <E2EAuthContextProvider>{children}</E2EAuthContextProvider> : <KeycloakAuthContextProvider>{children}</KeycloakAuthContextProvider>;
}

function KeycloakAuthContextProvider({ children }: { children: ReactNode }) {
  const { keycloak } = useKeycloak();
  const me = useMe();

  const value = useMemo<AuthValue>(() => {
    const realmRoles = (keycloak.realmAccess?.roles ?? []) as string[];
    const features = me.data?.features;
    return {
      isAuthenticated: Boolean(keycloak.authenticated),
      user: me.data?.user,
      tenant: me.data?.staffing_agency,
      roles: realmRoles,
      hasRole: (...allowed) => allowed.some((r) => realmRoles.includes(r)),
      hasFeature: (feature) => features?.[feature] ?? true,
      signOut: () => {
        clearTokens();
        void keycloak.logout();
      },
    };
  }, [keycloak, me.data]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * E2E variant: parses realm roles out of the test JWT (no Keycloak
 * client to read `realmAccess` from). Sign-out clears localStorage;
 * Playwright tears the rest down between runs.
 */
function E2EAuthContextProvider({ children }: { children: ReactNode }) {
  const me = useMe();

  const value = useMemo<AuthValue>(() => {
    const roles = readRolesFromStoredToken();
    const features = me.data?.features;
    return {
      isAuthenticated: Boolean(me.data?.user),
      user: me.data?.user,
      tenant: me.data?.staffing_agency,
      roles,
      hasRole: (...allowed) => allowed.some((r) => roles.includes(r)),
      hasFeature: (feature) => features?.[feature] ?? true,
      signOut: () => window.localStorage.removeItem('relay.e2e.token'),
    };
  }, [me.data]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function readRolesFromStoredToken(): string[] {
  if (typeof window === 'undefined') return [];
  const token = window.localStorage.getItem('relay.e2e.token');
  if (!token) return [];
  const parts = token.split('.');
  if (parts.length < 2) return [];
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { realm_access?: { roles?: string[] } };
    return payload.realm_access?.roles ?? [];
  } catch {
    return [];
  }
}

export function useAuth() {
  return useContext(AuthContext);
}
